import { useState, useEffect, useRef } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection, readOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { syncBidirectionalRefs } from '../../utils/relationSync';
import { callWorker } from '../../utils/workerClient';
import PeopleSelector from '../shared/PeopleSelector';
import AISuggestButton from '../shared/AISuggestButton';
import TagInput from '../shared/TagInput';
import EntitySelector from '../shared/EntitySelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import InfoBadge from '../shared/InfoBadge';
import { BG_SUBTLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, STATUS_WARN, STATUS_INFO, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_BUTTON} from '../../theme';
import { NAVBAR_HEIGHT } from '../../utils/constants';

const aId = (id) => id ? `A.${id}` : id;

// ─── SHARED HELPERS ───────────────────────────────────────────────────────────
// Single canonical risk-level computation per §4 Risk "Derived fields" and
// §11 rule 5 (derived fields are persisted and never re-derived at render).
// Returns the uppercase form that is persisted on the Risk record.
// `thresholds` is `org.riskMatrix.thresholds`; defaults {low: 4, medium: 9}
// per §4 coercion rules apply when the field is absent on the org profile.
export function levelFromScore(score, thresholds) {
  const { low = 4, medium = 9 } = thresholds || {};
  if (score <= low) return 'LOW';
  if (score <= medium) return 'MEDIUM';
  return 'HIGH';
}

export const LEVEL_COLORS = {
  LOW:    { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
  MEDIUM: { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  HIGH:   { bg: ERROR_BG, text: ERROR_TEXT },
};

// ─── TAB MODEL ──────────────────────────────────────────────────────────────
// The form's six stacked sections are presented in three tabs. This map links
// each validatable field key to the tab that owns it, so a failed validation
// can switch to the right tab and so the tab error indicator can light up.
// Only keys that validate() can actually produce today are listed (both checks
// — riskSummary and owner — belong to the Identification tab). Add new keys
// here as validate() grows; no other tab code needs to change.
const FIELD_TAB = {
  riskSummary: 'identification',
  owner: 'identification',
};

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate: 'Nuevo riesgo',
    titleEdit:   'Editar riesgo',
    titleView:   'Ver riesgo',
    backToList:  '← Volver al listado',
    s1: 'Identificación del riesgo',
    s2: 'Evaluación inicial (sin tratamiento)',
    s3: 'Plan de tratamiento',
    s4: 'Evaluación residual (con tratamiento)',
    s5: 'Aceptación y metadatos',
    s6: 'Relaciones',
    tabIdentification: 'Identificación',
    tabTreatment:      'Tratamiento',
    tabRelationships:  'Relaciones',
    fRef:               'Referencia',
    fSummary:           'Resumen del riesgo',
    fDescription:       'Descripción',
    fRiskType:          'Tipo de riesgo (CIA)',
    fCategory:          'Categoría',
    fOwner:             'Propietario',
    fExistingControls:  'Controles existentes (incluye ref. Anexo A si aplica)',
    fIdentifiedDate:       'Fecha de identificación',
    fIdentifiedBy:         'Identificado por',
    fTreatmentApprovedBy:   'Tratamiento aprobado por',
    fTreatmentApprovalDate: 'Fecha de aprobación del tratamiento',
    fLinkedAssetIds:          'Activos vinculados',
    fLinkedSupplierIds:       'Proveedores vinculados',
    fLinkedIncidentIds:       'Incidentes vinculados',
    fLinkedNonconformityIds:  'No conformidades vinculadas',
    placeholderIds:           'e.g. asset-20260101-001',
    fLikelihood:        'Probabilidad',
    fLikelihoodRat:     'Justificación de la probabilidad',
    fImpact:            'Impacto',
    fImpactRat:         'Justificación del impacto',
    fInherentScore:     'Puntuación inherente',
    fTreatmentOption:   'Opción de tratamiento',
    fProposedAction:    'Acción de tratamiento propuesta',
    fControlRefs:       'Referencias a controles',
    fActionOwner:       'Responsable de la acción',
    fTreatmentDeadline: 'Fecha límite del tratamiento',
    fProgress:          'Notas de progreso',
    fActionStatus:      'Estado de la acción',
    fLinkedControls:    'Controles del Anexo A vinculados',
    fResidualLik:       'Probabilidad residual',
    fResidualImp:       'Impacto residual',
    fResidualScore:     'Puntuación residual',
    s4LockedApproval:   'Completa la aprobación del tratamiento para desbloquear la evaluación post-tratamiento.',
    s4LockedAccept:     'Acepta el riesgo para desbloquear la evaluación post-tratamiento.',
    fAcceptanceCrit:    'Criterios de aceptación',
    fAccepted:          'Riesgo aceptado',
    fAcceptedBy:        'Aceptado por',
    fAcceptanceDate:    'Fecha de aceptación',
    fStatus:            'Estado',
    fNextReviewDate:         'Próxima fecha de revisión',
    fResidualRiskAccepted:   'Riesgo residual aceptado',
    fInterviewNotes:    'Notas de entrevista',
    ttRiskType: 'Propiedades de seguridad afectadas por el riesgo. Marca C (confidencialidad), I (integridad), A (disponibilidad) o varias si aplican.',
    ttCategory: 'Dominio temático del riesgo dentro del SGSI (p. ej. Control de Acceso, Cloud, Proveedores). Permite agrupar y filtrar riesgos similares en informes y revisiones.',
    ttExistingControls: 'Controles, procesos o salvaguardas ya operativos que mitigan parcialmente este riesgo en su forma actual. No se incluyen aquí controles previstos o pendientes de implementar.',
    ttIdentifiedDate: 'Fecha en que se identificó el riesgo y se registró formalmente en el inventario. No es la fecha de un incidente, sino la del análisis.',
    ttIdentifiedBy: 'Persona o rol que detectó o reportó el riesgo. Útil para trazabilidad y para consultar contexto adicional durante revisiones posteriores.',
    ttLikelihood: 'Probabilidad de que el escenario de riesgo se materialice en un horizonte definido (típicamente 12 meses), considerando los controles existentes ya documentados.',
    ttLikelihoodRat: 'Justificación de la probabilidad asignada: factores observables, frecuencia histórica, exposición actual o supuestos del consultor. Permite defender la evaluación ante auditoría externa.',
    ttImpact: 'Magnitud del daño si el riesgo se materializa, considerando aspectos legales, operativos, reputacionales y financieros combinados.',
    ttImpactRat: 'Justificación del impacto asignado: tipo y volumen de datos afectados, consecuencias legales potenciales, coste operativo de la disrupción, daño reputacional estimado.',
    ttInherentScore: 'Nivel de riesgo antes de aplicar tratamiento adicional: probabilidad × impacto. Refleja el riesgo expuesto considerando solo los controles ya existentes.',
    ttTreatmentOption: 'Estrategia para abordar el riesgo según ISO 27005: Modificar (reducir con controles), Evitar (eliminar la actividad), Compartir (transferir o asegurar), Retener (aceptar el riesgo residual).',
    ttProposedAction: 'Plan de acción concreto para implementar el tratamiento elegido. Cada paso debe ser específico, asignable y verificable; sin generalidades.',
    ttControlRefs: 'Referencias a controles aplicables provenientes de varios marcos (p. ej. iso27001:A.5.1, nist:PR.AC-1). Visible solo cuando la organización opera con más de una norma simultáneamente.',
    ttLinkedControls: 'Controles del Anexo A de ISO 27001:2022 que mitigan directamente este riesgo. Solo se muestran controles marcados como aplicables en el SoA.',
    ttTreatmentApprovedBy: 'Persona con autoridad formal que aprueba el plan de tratamiento antes de ejecutarlo. Requisito de gobernanza para riesgos significativos.',
    ttTreatmentApprovalDate: 'Fecha en que se aprobó formalmente el plan de tratamiento. Establece el punto desde el que cuenta el cumplimiento de los plazos comprometidos.',
    ttTreatmentDeadline: 'Fecha límite para completar las acciones de tratamiento. Plazos vencidos sin completar son hallazgos en auditorías internas y externas.',
    ttProgress: 'Porcentaje de avance en la ejecución del plan de tratamiento. Actualizar periódicamente (al menos trimestralmente) para reflejar el estado real.',
    ttResidualLik: 'Probabilidad estimada de materialización una vez aplicado el tratamiento elegido. Debe ser igual o menor que la probabilidad inherente.',
    ttResidualImp: 'Magnitud estimada del daño una vez aplicado el tratamiento elegido. Debe ser igual o menor que el impacto inherente.',
    ttResidualScore: 'Nivel de riesgo tras aplicar tratamiento: probabilidad residual × impacto residual. Si supera el umbral de aceptación, se requiere tratamiento adicional o aceptación formal.',
    ttAcceptanceCrit: 'Condiciones bajo las cuales el riesgo residual se considera aceptable. Pueden ser cuantitativas (umbral de score), cualitativas (criterios de negocio) o regulatorias.',
    ttAcceptedBy: 'Persona con autoridad formal que acepta el riesgo residual. Suele ser el propietario del riesgo o la dirección, según el nivel de exposición.',
    ttAcceptanceDate: 'Fecha en que se aceptó formalmente el riesgo residual. Necesaria como evidencia documental ante auditoría y para programar la próxima revisión.',
    ttStatus: 'Estado actual del riesgo en el ciclo de vida del SGSI. Refleja la fase en la que se encuentra: análisis, tratamiento, aceptado, cerrado u obsoleto.',
    ttNextReviewDate: 'Fecha prevista para la próxima revisión periódica del riesgo. ISO 27001 exige revisión a intervalos planificados; típicamente anual o cuando cambia el contexto.',
    ttResidualRiskAccepted: 'Marca este riesgo como formalmente aceptado tras el tratamiento. Requiere haber registrado el nivel residual, los criterios de aceptación, el aprobador y la fecha.',
    ttInterviewNotes: 'Notas del consultor durante entrevistas con stakeholders sobre este riesgo. Soporte interno; no se incluye en informes para el cliente.',
    aiClassificationHeading: 'Clasificación del riesgo',
    showNotes:          'Mostrar notas de entrevista',
    hideNotes:          'Ocultar notas de entrevista',
    liLabels:  ['Improbable', 'Poco probable', 'Probable', 'Muy probable', 'Casi seguro'],
    imLabels:  ['Insignificante', 'Leve', 'Moderado', 'Alto', 'Muy alto'],
    levelLow:    'BAJO',
    levelMedium: 'MEDIO',
    levelHigh:   'ALTO',
    btnSave:     'Guardar',
    btnCancel:   'Cancelar',
    btnDelete:   'Eliminar riesgo',
    btnConfirmDelete: '¿Confirmar eliminación?',
    btnCancelDelete:  'No, cancelar',
    saving:      'Guardando…',
    saveOk:      '✓ Guardado',
    errorRequired: 'Campo obligatorio',
    noticeApproverEqualsOwner: 'El aprobador del tratamiento coincide con el propietario del riesgo. La segregación de funciones (A.5.3) es recomendable cuando sea viable; en organizaciones pequeñas puede compensarse con supervisión de dirección y auditoría interna.',
    fSodException:  'Justificación de la excepción (segregación de funciones)',
    ttSodException: 'Explica por qué el aprobador del tratamiento coincide con el propietario y qué controles compensatorios aplican (p. ej. supervisión de dirección, auditoría interna). Si se rellena, esta excepción deja de señalarse como brecha en el panel de riesgos.',
    errorSave:     'Error al guardar. Inténtalo de nuevo.',
    errorDelete:   'Error al eliminar.',
    placeholderControlIds: 'e.g. 5.1, 5.15, 8.3',
    placeholderControlRefs: 'iso27001:A.5.1',
    searchControl:         'Buscar control…',
    categoryOptions: ['Access Control','Cloud Infrastructure','Data Protection','Physical Security','Human Resources','Business Continuity','Supplier','Compliance','Identity & Access Management','Vulnerability Management','Network Security','Endpoint Security','Application Security','Incident Response','Change Management','Cryptography','Audit & Logging','Legal & Regulatory','Other'],
    statusOptions: [
      { value: 'Identified',    label: 'Identificado' },
      { value: 'Being Treated', label: 'En tratamiento' },
      { value: 'Treated',       label: 'Tratado' },
      { value: 'Accepted',      label: 'Aceptado' },
      { value: 'Closed',        label: 'Cerrado' },
    ],
    actionStatusOptions: [
      { value: 'Not Started', label: 'No iniciado' },
      { value: 'In Progress', label: 'En curso' },
      { value: 'Completed',   label: 'Completado' },
    ],
    treatmentOptions: [
      { value: 'Modify', label: 'Modificar' },
      { value: 'Avoid',  label: 'Evitar' },
      { value: 'Share',  label: 'Compartir' },
      { value: 'Retain', label: 'Retener' },
    ],
    required: '*',
    requiredLegend:      '* Campos obligatorios',
    errorRequiredFields: 'Por favor completa los campos obligatorios antes de guardar.',
    btnAISuggest: 'Sugerir con IA',
    aiSuggesting: 'Sugiriendo…',
    aiError:         'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded: 'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired: 'Activa el procesamiento con IA en Configuración para usar esta función.',
    aiPromptScopeTitle: 'Sugerir tratamiento con IA',
    aiPromptScopeIntro: 'Selecciona qué contexto enviar a la IA:',
    aiPromptScopeBasic: 'Solo metadatos estructurados (recomendado)',
    aiPromptScopeExtended: 'Incluir descripción y justificaciones',
    aiPromptScopeExtendedHelp: 'Estos campos pueden contener nombres de personas, clientes o detalles internos. Revisa el contenido antes de continuar.',
    aiPromptScopeSend: 'Enviar',
    aiPromptScopeCancel: 'Cancelar',
    unsavedLabel:    'Cambios sin guardar',
  },
  en: {
    titleCreate: 'New risk',
    titleEdit:   'Edit risk',
    titleView:   'View risk',
    backToList:  '← Back to list',
    s1: 'Risk identification',
    s2: 'Pre-treatment assessment',
    s3: 'Treatment plan',
    s4: 'Post-treatment assessment',
    s5: 'Acceptance and metadata',
    s6: 'Relationships',
    tabIdentification: 'Identification',
    tabTreatment:      'Treatment',
    tabRelationships:  'Relationships',
    fRef:               'Reference',
    fSummary:           'Risk summary',
    fDescription:       'Description',
    fRiskType:          'Risk type (CIA)',
    fCategory:          'Category',
    fOwner:             'Owner',
    fExistingControls:  'Existing controls (include Annex A ref if applicable)',
    fIdentifiedDate:       'Identified date',
    fIdentifiedBy:         'Identified by',
    fTreatmentApprovedBy:   'Treatment approved by',
    fTreatmentApprovalDate: 'Treatment approval date',
    fLinkedAssetIds:          'Linked assets',
    fLinkedSupplierIds:       'Linked suppliers',
    fLinkedIncidentIds:       'Linked incidents',
    fLinkedNonconformityIds:  'Linked nonconformities',
    placeholderIds:           'e.g. asset-20260101-001',
    fLikelihood:        'Likelihood',
    fLikelihoodRat:     'Likelihood rationale',
    fImpact:            'Impact',
    fImpactRat:         'Impact rationale',
    fInherentScore:     'Inherent score',
    fTreatmentOption:   'Treatment option',
    fProposedAction:    'Proposed treatment action',
    fControlRefs:       'Control references',
    fActionOwner:       'Treatment action owner',
    fTreatmentDeadline: 'Treatment deadline',
    fProgress:          'Progress notes',
    fActionStatus:      'Action status',
    fLinkedControls:    'Linked Annex A controls',
    fResidualLik:       'Residual likelihood',
    fResidualImp:       'Residual impact',
    fResidualScore:     'Residual score',
    s4LockedApproval:   'Complete treatment approval to unlock post-treatment assessment.',
    s4LockedAccept:     'Accept the risk to unlock post-treatment assessment.',
    fAcceptanceCrit:    'Acceptance criteria',
    fAccepted:          'Risk accepted',
    fAcceptedBy:        'Accepted by',
    fAcceptanceDate:    'Acceptance date',
    fStatus:            'Status',
    fNextReviewDate:         'Next review date',
    fResidualRiskAccepted:   'Residual risk accepted',
    fInterviewNotes:    'Interview notes',
    ttRiskType: 'Security properties affected by the risk. Mark C (confidentiality), I (integrity), A (availability), or several if applicable.',
    ttCategory: 'Thematic domain of the risk within the ISMS (e.g. Access Control, Cloud, Suppliers). Used to group and filter similar risks in reports and reviews.',
    ttExistingControls: 'Controls, processes or safeguards already in place that partially mitigate this risk as currently formulated. Do not include controls planned or pending implementation.',
    ttIdentifiedDate: 'Date the risk was identified and formally recorded in the register. Not the date of any incident — the date of analysis.',
    ttIdentifiedBy: 'Person or role who detected or reported the risk. Useful for traceability and for clarifying context during later reviews.',
    ttLikelihood: 'Probability that the risk scenario materializes within a defined horizon (typically 12 months), considering the existing controls already documented.',
    ttLikelihoodRat: 'Justification of the assigned likelihood: observable factors, historical frequency, current exposure, or consultant assumptions. Supports defending the evaluation under external audit.',
    ttImpact: 'Magnitude of damage if the risk materializes, considering legal, operational, reputational, and financial dimensions combined.',
    ttImpactRat: 'Justification of the assigned impact: type and volume of affected data, potential legal consequences, operational cost of disruption, estimated reputational damage.',
    ttInherentScore: 'Risk level before applying additional treatment: likelihood × impact. Reflects the exposed risk considering only the existing controls already in place.',
    ttTreatmentOption: 'Strategy to address the risk per ISO 27005: Modify (reduce with controls), Avoid (remove the activity), Share (transfer or insure), Retain (accept the residual risk).',
    ttProposedAction: 'Concrete action plan to implement the chosen treatment. Each step must be specific, assignable, and verifiable; no vague generalities.',
    ttControlRefs: 'References to applicable controls from multiple frameworks (e.g. iso27001:A.5.1, nist:PR.AC-1). Shown only when the organization operates with more than one norm simultaneously.',
    ttLinkedControls: 'ISO 27001:2022 Annex A controls that directly mitigate this risk. Only controls flagged as applicable in the SoA are shown.',
    ttTreatmentApprovedBy: 'Person with formal authority who approves the treatment plan before execution. A governance requirement for significant risks.',
    ttTreatmentApprovalDate: 'Date the treatment plan was formally approved. Sets the reference point from which committed deadlines are counted.',
    ttTreatmentDeadline: 'Deadline to complete treatment actions. Overdue plans are findings in both internal and external audits.',
    ttProgress: 'Percentage of progress in executing the treatment plan. Update periodically (at least quarterly) to reflect the actual state.',
    ttResidualLik: 'Estimated likelihood of materialization after the chosen treatment is applied. Must be equal to or lower than the inherent likelihood.',
    ttResidualImp: 'Estimated magnitude of damage after the chosen treatment is applied. Must be equal to or lower than the inherent impact.',
    ttResidualScore: 'Risk level after treatment: residual likelihood × residual impact. If it exceeds the acceptance threshold, additional treatment or formal acceptance is required.',
    ttAcceptanceCrit: 'Conditions under which the residual risk is considered acceptable. May be quantitative (score threshold), qualitative (business criteria), or regulatory.',
    ttAcceptedBy: 'Person with formal authority who accepts the residual risk. Typically the risk owner or senior management, depending on exposure level.',
    ttAcceptanceDate: 'Date the residual risk was formally accepted. Required as documentary evidence for audit and to schedule the next review.',
    ttStatus: 'Current state of the risk in the ISMS lifecycle. Reflects which phase the risk is in: assessment, treatment, accepted, closed, or obsolete.',
    ttNextReviewDate: 'Planned date for the next periodic review of the risk. ISO 27001 requires review at planned intervals; typically annual or when context changes.',
    ttResidualRiskAccepted: 'Marks this risk as formally accepted following treatment. Requires the residual level, acceptance criteria, approver, and date to have been recorded.',
    ttInterviewNotes: 'Consultant notes from stakeholder interviews about this risk. Internal support; not included in client-facing reports.',
    aiClassificationHeading: 'Risk classification',
    showNotes:          'Show interview notes',
    hideNotes:          'Hide interview notes',
    liLabels:  ['Improbable', 'Unlikely', 'Likely', 'Very Likely', 'Almost Certain'],
    imLabels:  ['Negligible', 'Slight', 'Moderate', 'High', 'Very High'],
    levelLow:    'LOW',
    levelMedium: 'MEDIUM',
    levelHigh:   'HIGH',
    btnSave:     'Save',
    btnCancel:   'Cancel',
    btnDelete:   'Delete risk',
    btnConfirmDelete: 'Confirm delete?',
    btnCancelDelete:  'No, cancel',
    saving:      'Saving…',
    saveOk:      '✓ Saved',
    errorRequired: 'Required',
    noticeApproverEqualsOwner: 'The treatment approver is the same as the risk owner. Segregation of duties (A.5.3) is advisable where feasible; in small organizations it can be compensated with management oversight and internal audit.',
    fSodException:  'Exception justification (segregation of duties)',
    ttSodException: 'Explain why the treatment approver is the same as the risk owner and what compensating controls apply (e.g. management oversight, internal audit). When filled in, this exception is no longer flagged as a gap in the risk panel.',
    errorSave:     'Save failed. Please try again.',
    errorDelete:   'Delete failed.',
    placeholderControlIds: 'e.g. 5.1, 5.15, 8.3',
    placeholderControlRefs: 'iso27001:A.5.1',
    searchControl:         'Search control…',
    categoryOptions: ['Access Control','Cloud Infrastructure','Data Protection','Physical Security','Human Resources','Business Continuity','Supplier','Compliance','Identity & Access Management','Vulnerability Management','Network Security','Endpoint Security','Application Security','Incident Response','Change Management','Cryptography','Audit & Logging','Legal & Regulatory','Other'],
    statusOptions: [
      { value: 'Identified',    label: 'Identified' },
      { value: 'Being Treated', label: 'Being Treated' },
      { value: 'Treated',       label: 'Treated' },
      { value: 'Accepted',      label: 'Accepted' },
      { value: 'Closed',        label: 'Closed' },
    ],
    actionStatusOptions: [
      { value: 'Not Started', label: 'Not Started' },
      { value: 'In Progress', label: 'In Progress' },
      { value: 'Completed',   label: 'Completed' },
    ],
    treatmentOptions: [
      { value: 'Modify', label: 'Modify' },
      { value: 'Avoid',  label: 'Avoid' },
      { value: 'Share',  label: 'Share' },
      { value: 'Retain', label: 'Retain' },
    ],
    required: '*',
    requiredLegend:      '* Required fields',
    errorRequiredFields: 'Please fill in all required fields before saving.',
    btnAISuggest: 'Suggest with AI',
    aiSuggesting: 'Suggesting…',
    aiError:         'Could not get suggestion. Please try again.',
    aiErrorOverloaded: 'Servers are busy, please try again in a moment.',
    aiConsentRequired: 'Enable AI processing in Settings to use this feature.',
    aiPromptScopeTitle: 'Suggest treatment with AI',
    aiPromptScopeIntro: 'Select what context to send to the AI:',
    aiPromptScopeBasic: 'Structured metadata only (recommended)',
    aiPromptScopeExtended: 'Include description and rationales',
    aiPromptScopeExtendedHelp: 'These fields may contain names of people, clients, or internal details. Review the content before proceeding.',
    aiPromptScopeSend: 'Send',
    aiPromptScopeCancel: 'Cancel',
    unsavedLabel:    'Unsaved changes',
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function yyyymmdd() {
  return todayStr().replace(/-/g, '');
}

async function generateIds(orgId, riskPrefix) {
  const year = new Date().getFullYear();
  const day = yyyymmdd();
  let risks = [];
  try { risks = await listOrgCollection(orgId, 'risks'); } catch { /* empty */ }

  // File ID: risk-YYYYMMDD-seq
  const dayPrefix = `risk-${day}-`;
  let maxFileSeq = 0;
  risks.forEach((r) => {
    if (r.id && r.id.startsWith(dayPrefix)) {
      const n = parseInt(r.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
    }
  });
  const fileId = `${dayPrefix}${String(maxFileSeq + 1).padStart(3, '0')}`;

  // Ref: {prefix}-YYYY-seq
  const refPrefix = `${riskPrefix}-${year}-`;
  let maxRefSeq = 0;
  risks.forEach((r) => {
    if (r.ref && r.ref.startsWith(refPrefix)) {
      const n = parseInt(r.ref.slice(refPrefix.length), 10);
      if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
    }
  });
  const ref = `${refPrefix}${String(maxRefSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

function levelLabel(level, t) {
  if (level === 'LOW') return t.levelLow;
  if (level === 'MEDIUM') return t.levelMedium;
  return t.levelHigh;
}

// ─── SHARED STYLE HELPERS ─────────────────────────────────────────────────────
const labelStyle = {
  ...FONT_SMALL,
  display: 'block',
  color: TEXT_MUTED,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  marginBottom: '0.3rem',
};

const inputStyle = (hasError) => ({
  ...FONT_BODY,
  width: '100%',
  padding: '0.55rem 0.7rem',
  border: `1px solid ${hasError ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: SURFACE_WHITE,
  color: TEXT_HEADING,
  outline: 'none',
  boxSizing: 'border-box',
});

const textareaStyle = (hasError) => ({
  ...inputStyle(hasError),
  resize: 'vertical',
  minHeight: 72,
});

const selectStyle = {
  ...inputStyle(false),
  cursor: 'pointer',
};

const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem',
  paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};

function FieldBlock({ children, span2 }) {
  return (
    <div style={{ gridColumn: span2 ? 'span 2' : undefined, marginBottom: '1rem' }}>
      {children}
    </div>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>
      {msg}
    </p>
  );
}

// ─── LEVEL/SCORE BADGE ────────────────────────────────────────────────────────
// `level` is the canonical uppercase level (LOW/MEDIUM/HIGH). Callers derive
// it via levelFromScore(score, org.riskMatrix.thresholds) using the single
// canonical function in this module — never re-derive inline here.
function ScoreBadge({ score, level, t }) {
  const colors = LEVEL_COLORS[level];
  return (
    <span style={{
      ...FONT_LABEL,
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.4rem',
      background: colors.bg,
      color: colors.text,
      borderRadius: 6,
      padding: '0.25rem 0.6rem',
      fontWeight: 600,
    }}>
      {score} · {levelLabel(level, t)}
    </span>
  );
}

// ─── RATING SELECTOR (1–5 buttons) ────────────────────────────────────────────
function RatingSelector({ value, onChange, labels, disabled }) {
  return (
    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          disabled={disabled}
          style={{
            ...FONT_SMALL,
            background: value === n ? TEXT_HEADING : SURFACE_WHITE,
            color: value === n ? SURFACE_WHITE : TEXT_DEFAULT,
            border: `1px solid ${value === n ? TEXT_HEADING : BORDER_DEFAULT}`,
            borderRadius: 6,
            padding: '0.35rem 0.5rem',
            cursor: disabled ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            transition: 'all 0.12s',
          }}
        >
          {n} – {labels[n - 1]}
        </button>
      ))}
    </div>
  );
}

// ─── INITIAL FORM STATE ────────────────────────────────────────────────────────
const EMPTY = {
  riskSummary: '',
  description: '',
  riskType: [],
  category: '',
  owner: '',
  existingControls: '',
  identifiedDate: null,
  identifiedBy: '',
  likelihood: 3,
  likelihoodRationale: '',
  impact: 3,
  impactRationale: '',
  treatmentOption: 'Modify',
  proposedTreatmentAction: '',
  controlReferences: [],
  treatmentActionOwner: [],
  treatmentApprovedBy: '',
  sodExceptionJustification: '',
  treatmentApprovalDate: null,
  treatmentDeadline: null,
  treatmentActionProgress: '',
  treatmentActionStatus: 'Not Started',
  linkedControlIds: [],
  residualLikelihood: null,
  residualImpact: null,
  residualScore: null,
  acceptanceCriteria: '',
  accepted: false,
  acceptedBy: '',
  acceptanceDate: '',
  status: 'Identified',
  nextReviewDate: null,
  residualRiskAccepted: false,
  interviewNotes: '',
  linkedAssetIds: [],
  linkedSupplierIds: [],
  linkedIncidentIds: [],
  linkedNonconformityIds: [],
};

function riskToForm(risk) {
  return {
    riskSummary: risk.riskSummary || risk.title || '',
    description: risk.description || '',
    riskType: Array.isArray(risk.riskType) ? risk.riskType : [],
    category: risk.category || '',
    owner: risk.owner || '',
    existingControls: risk.existingControls || '',
    identifiedDate: risk.identifiedDate || null,
    identifiedBy: risk.identifiedBy || '',
    likelihood: risk.likelihood || 3,
    likelihoodRationale: risk.likelihoodRationale || '',
    impact: risk.impact || 3,
    impactRationale: risk.impactRationale || '',
    treatmentOption: risk.treatmentOption || 'Modify',
    proposedTreatmentAction: risk.proposedTreatmentAction || '',
    controlReferences: Array.isArray(risk.controlReferences) ? risk.controlReferences : [],
    treatmentActionOwner: Array.isArray(risk.treatmentActionOwner)
      ? risk.treatmentActionOwner
      : (typeof risk.treatmentActionOwner === 'string' && risk.treatmentActionOwner !== ''
          ? [risk.treatmentActionOwner]
          : []),
    treatmentApprovedBy: risk.treatmentApprovedBy || '',
    sodExceptionJustification: risk.sodExceptionJustification || '',
    treatmentApprovalDate: risk.treatmentApprovalDate || null,
    treatmentDeadline: risk.treatmentDeadline || risk.treatmentActionTimescale || null,
    treatmentActionProgress: risk.treatmentActionProgress || '',
    treatmentActionStatus: risk.treatmentActionStatus || 'Not Started',
    linkedControlIds: Array.isArray(risk.linkedControlIds) ? risk.linkedControlIds : (Array.isArray(risk.controlIds) ? risk.controlIds : []),
    residualLikelihood: risk.residualLikelihood ?? null,
    residualImpact: risk.residualImpact ?? null,
    residualScore: risk.residualScore ?? null,
    acceptanceCriteria: risk.acceptanceCriteria || '',
    accepted: !!risk.accepted,
    acceptedBy: risk.acceptedBy || '',
    acceptanceDate: risk.acceptanceDate || '',
    status: risk.status || 'Identified',
    nextReviewDate: risk.nextReviewDate || null,
    residualRiskAccepted: typeof risk.residualRiskAccepted === 'boolean' ? risk.residualRiskAccepted : false,
    interviewNotes: risk.interviewNotes || '',
    linkedAssetIds: Array.isArray(risk.linkedAssetIds) ? risk.linkedAssetIds : [],
    linkedSupplierIds: Array.isArray(risk.linkedSupplierIds) ? risk.linkedSupplierIds : [],
    linkedIncidentIds: Array.isArray(risk.linkedIncidentIds) ? risk.linkedIncidentIds : [],
    linkedNonconformityIds: Array.isArray(risk.linkedNonconformityIds) ? risk.linkedNonconformityIds : [],
  };
}

// Silent migration for the appendChangeLog diff. Existing risk records on disk
// may still carry legacy keys removed in the 2026-05-12 ghost-field cleanup;
// stripping them from the previous snapshot prevents spurious "value → undefined"
// entries in the audit trail when an old record is edited.
const LEGACY_RISK_FIELDS = [
  'threat', 'vulnerability', 'isOpportunity', 'treatmentCost',
  'treatmentStartDate', 'firstIdentifiedDate', 'identificationMethod',
  'reviewDate', 'residualRiskAcceptedBy', 'residualRiskAcceptedDate',
  'norms', 'asset',
];
function stripLegacyRiskFields(record) {
  const clone = { ...record };
  for (const k of LEGACY_RISK_FIELDS) delete clone[k];
  return clone;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function RiskForm({ mode, risk, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty }) {
  const t = T[lang] || T.en;
  const [form, setForm] = useState(() => (mode === 'edit' || mode === 'view') && risk ? riskToForm(risk) : EMPTY);
  const [fileId, setFileId] = useState(mode === 'edit' && risk ? risk.id : null);
  const [ref, setRef] = useState(mode === 'edit' && risk ? risk.ref : null);
  const [currentMode, setCurrentMode] = useState(mode);
  const [savedRecord, setSavedRecord] = useState(mode === 'edit' ? risk : null);
  const hasChangedRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState('idle');
  const [saveErrorOpen, setSaveErrorOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuggestingTreatment, setAiSuggestingTreatment] = useState(false);
  const [aiErrorTreatment, setAiErrorTreatment] = useState(null);
  // AI scope dialog. Clicking the Section 3 AI button always opens this
  // dialog; the consultant picks "basic" (Layer 1 only) or "extended" (also
  // sends the four sensitive free-text fields) per call. Default is "basic"
  // and the choice is not persisted — every open resets to the safe default.
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiPromptScope, setAiPromptScope] = useState('basic');
  const [controlOptions,   setControlOptions]   = useState([]);
  const [assetOptions,     setAssetOptions]     = useState([]);
  const [supplierOptions,  setSupplierOptions]  = useState([]);
  const [incidentOptions,  setIncidentOptions]  = useState([]);
  const [ncOptions,        setNcOptions]        = useState([]);
  // Active tab. The form is remounted whenever a different risk is opened, so a
  // plain initial value is enough to satisfy "always opens on Identification" —
  // no reset effect, no localStorage/sessionStorage.
  const [activeTab, setActiveTab] = useState('identification');

  // Load SoA controls and relationship entity options for EntitySelector
  useEffect(() => {
    readOrgFile(org.id, 'controls/soa.json')
      .then((soa) => setControlOptions((soa.controls || []).map((c) => ({ id: c.id, label: `${aId(c.id)} — ${lang === 'es' ? c.title_es : c.title}` }))))
      .catch(() => setControlOptions([]));
    listOrgCollection(org.id, 'assets')
      .then((list) => setAssetOptions(list.map((a) => { const base = a.name || a.ref || a.id; return { id: a.id, label: a.type ? `${base} (${a.type})` : base }; })))
      .catch(() => setAssetOptions([]));
    listOrgCollection(org.id, 'suppliers')
      .then((list) => setSupplierOptions(list.map((s) => ({ id: s.id, label: s.name || s.ref || s.id }))))
      .catch(() => setSupplierOptions([]));
    listOrgCollection(org.id, 'incidents')
      .then((list) => setIncidentOptions(list.map((i) => ({ id: i.id, label: i.title || i.ref || i.id }))))
      .catch(() => setIncidentOptions([]));
    listOrgCollection(org.id, 'nonconformities')
      .then((list) => setNcOptions(list.map((nc) => ({ id: nc.id, label: nc.ref || nc.id }))))
      .catch(() => setNcOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id, lang]);

  // Window resize
  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Generate IDs for create mode
  useEffect(() => {
    if (mode !== 'create') return;
    const riskPrefix = (org.refPrefixes && org.refPrefixes.risk) || 'rsk';
    generateIds(org.id, riskPrefix).then(({ fileId: fid, ref: r }) => {
      setFileId(fid);
      setRef(r);
    }).catch(() => {
      const ts = Date.now().toString().slice(-4);
      setFileId(`risk-${yyyymmdd()}-${ts}`);
      setRef(`${riskPrefix}-${new Date().getFullYear()}-${ts}`);
    });
  }, [mode, org.id, org.refPrefixes]);

  // Keep residualScore in sync. Residual values are null until the treatment
  // is approved (or the risk is accepted, for Retain), so guard the product.
  const inherentScore = form.likelihood * form.impact;
  const residualScore = (form.residualLikelihood != null && form.residualImpact != null)
    ? form.residualLikelihood * form.residualImpact
    : null;
  // Single canonical risk-level computation (§4 "Derived fields", §11 rule 5).
  // Reads org.riskMatrix.thresholds; spec default fallback lives in levelFromScore.
  const thresholds = org.riskMatrix && org.riskMatrix.thresholds;
  const inherentLevel = levelFromScore(inherentScore, thresholds);
  const residualLevel = residualScore != null ? levelFromScore(residualScore, thresholds) : null;

  // Post-treatment assessment is gated by treatment approval. For Retain, the
  // meaningful timestamp is acceptance; for Modify/Avoid/Share, it is
  // treatmentApprovalDate. Until the gate opens, residual fields stay null
  // and Section 4 is replaced by a read-only notice.
  const postTreatmentUnlocked = form.treatmentOption === 'Retain'
    ? form.accepted === true
    : form.treatmentApprovalDate != null;

  const markChange = () => {
    if (mode !== 'view' && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const setField = (field) => (val) => {
    markChange();
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleInputChange = (field) => (e) => setField(field)(e.target.value);

  const toggleCIA = (code) => {
    markChange();
    setForm((prev) => ({
      ...prev,
      riskType: prev.riskType.includes(code)
        ? prev.riskType.filter((c) => c !== code)
        : [...prev.riskType, code],
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.riskSummary.trim()) errs.riskSummary = t.errorRequired;
    if (!form.owner.trim()) errs.owner = t.errorRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const first = ['riskSummary', 'owner'].find((f) => errs[f]);
      if (first) {
        // Switch to the tab that owns the first failing field before scrolling.
        // The tab switch is unconditional; the scroll stays guarded so a field
        // with no anchor in the DOM (e.g. owner) does not throw.
        const targetTab = FIELD_TAB[first];
        if (targetTab) setActiveTab(targetTab);
        const el = document.getElementById(`risk-field-${first}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    setSaveState('saving');
    try {
      const now = new Date().toISOString();
      const existing = savedRecord;
      const payload = {
        id: fileId,
        orgId: org.id,
        ref,
        riskSummary: form.riskSummary,
        description: form.description,
        riskType: form.riskType,
        category: form.category,
        existingControls: form.existingControls,
        identifiedDate: form.identifiedDate || null,
        identifiedBy: form.identifiedBy || null,
        owner: form.owner,
        likelihood: form.likelihood,
        likelihoodRationale: form.likelihoodRationale,
        impact: form.impact,
        impactRationale: form.impactRationale,
        inherentScore,
        riskLevel: inherentLevel,
        treatmentOption: form.treatmentOption,
        proposedTreatmentAction: form.proposedTreatmentAction,
        controlReferences: form.controlReferences,
        treatmentActionOwner: form.treatmentActionOwner,
        treatmentApprovedBy: form.treatmentApprovedBy || null,
        // Segregation-of-duties exception justification: meaningful only while
        // the approver-equals-owner condition holds. When the condition does
        // not hold (no approver, no owner, or they differ), force empty so a
        // justification cannot linger on a risk whose approver no longer equals
        // its owner. When it holds, persist the raw form value (no trimming,
        // matching acceptanceCriteria).
        sodExceptionJustification: (form.treatmentApprovedBy && form.owner && form.treatmentApprovedBy === form.owner)
          ? form.sodExceptionJustification
          : '',
        treatmentApprovalDate: form.treatmentApprovalDate || null,
        treatmentDeadline: form.treatmentDeadline || null,
        treatmentActionProgress: form.treatmentActionProgress,
        treatmentActionStatus: form.treatmentActionStatus,
        linkedControlIds: form.linkedControlIds,
        residualLikelihood: postTreatmentUnlocked ? form.residualLikelihood : null,
        residualImpact: postTreatmentUnlocked ? form.residualImpact : null,
        residualScore: postTreatmentUnlocked ? residualScore : null,
        residualRiskLevel: postTreatmentUnlocked && residualScore != null ? residualLevel : null,
        acceptanceCriteria: form.treatmentOption === 'Avoid' ? '' : form.acceptanceCriteria,
        // Retain-only acceptance fields: meaningful only for Retain (where
        // `accepted` is the gate that unlocks the post-treatment assessment).
        // For any other treatment option, normalize away any stale state a
        // record may carry from when these fields were visible for it.
        accepted: form.treatmentOption === 'Retain' ? form.accepted : false,
        acceptedBy: form.treatmentOption === 'Retain' && form.accepted ? form.acceptedBy : null,
        acceptanceDate: form.treatmentOption === 'Retain' && form.accepted ? form.acceptanceDate : null,
        status: form.status,
        nextReviewDate: form.nextReviewDate || null,
        // Residual-acceptance field: meaningful only for Modify/Share. Retain has
        // no distinct residual and Avoid removes the activity, so persist false.
        residualRiskAccepted: (form.treatmentOption === 'Modify' || form.treatmentOption === 'Share')
          ? form.residualRiskAccepted
          : false,
        interviewNotes: form.interviewNotes || null,
        linkedAssetIds: Array.isArray(form.linkedAssetIds) ? form.linkedAssetIds : [],
        linkedSupplierIds: Array.isArray(form.linkedSupplierIds) ? form.linkedSupplierIds : [],
        linkedIncidentIds: Array.isArray(form.linkedIncidentIds) ? form.linkedIncidentIds : [],
        linkedNonconformityIds: Array.isArray(form.linkedNonconformityIds) ? form.linkedNonconformityIds : [],
        createdAt: existing?.createdAt || now,
        updatedAt: now,
      };
      payload.changeLog = existing ? appendChangeLog(stripLegacyRiskFields(existing), payload, org.consultantPersonId || 'system') : [];

      await writeOrgFile(org.id, `risks/${fileId}.json`, payload);
      try {
        await syncBidirectionalRefs({
          orgId: org.id,
          entityId: payload.id,
          otherCollection: 'nonconformities',
          otherIdsField: 'linkedRiskIds',
          previousOtherIds: existing?.linkedNonconformityIds || [],
          currentOtherIds: payload.linkedNonconformityIds,
          changedBy: org.consultantPersonId || 'system',
          isOtherImmutable: (nc) => nc.status === 'Closed',
        });
      } catch (syncErr) {
        console.warn('[RiskForm] bidirectional sync failed:', syncErr);
      }
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');

      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      // §11 rule 37 — blocking dialog on per-entity write failure.
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // §11 rule 37 — discard: revert to last saved snapshot and exit the form.
  // dirty stays true until the user picks Retry or Discard.
  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(riskToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
    }
    onCancel();
  };
  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await deleteOrgFile(org.id, `risks/${fileId}.json`);
      onSave(null);
    } catch {
      setDeleteError(t.errorDelete);
      setConfirmState(null);
    }
  };

  const handleAISuggest = async () => {
    setAiSuggesting(true);
    setAiError(null);
    const prompt = `You are an ISO 27001:2022 information security risk expert.\nGiven the following risk, suggest the most appropriate values.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nRisk summary: ${form.riskSummary}\nDescription: ${form.description}\nKnown categories: Access Control, Cloud Infrastructure, Data Protection, Physical Security, Human Resources, Business Continuity, Supplier, Compliance, Identity & Access Management, Vulnerability Management, Network Security, Endpoint Security, Application Security, Incident Response, Change Management, Cryptography, Audit & Logging, Legal & Regulatory, Other.\nIf the risk fits one of the listed categories, return that exact string. If none fits well, return the most specific and accurate category name for this risk. Only return "Other" if the risk genuinely does not belong to any more specific category.\ncontrolReferences must be an array of namespaced ISO 27001:2022 Annex A control references in the format "iso27001:A.5.1".\nReturn exactly this JSON structure:\n{\n"riskType": ["C","I","A"],\n"category": "string",\n"controlReferences": ["iso27001:A.5.1"]\n}`;
    const result = await callWorker({ prompt, maxTokens: 500 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiError(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggesting(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiError(t.aiError);
      setAiSuggesting(false);
      return;
    }
    if (Array.isArray(parsed.riskType) && !form.riskType.length) setField('riskType')(parsed.riskType);
    if (parsed.category && !form.category) setField('category')(parsed.category);
    if (Array.isArray(parsed.controlReferences) && form.controlReferences.length === 0) setField('controlReferences')(parsed.controlReferences);
    setAiSuggesting(false);
  };

  const handleAISuggestTreatment = async (scope) => {
    setAiSuggestingTreatment(true);
    setAiErrorTreatment(null);

    // Load the applicable-controls set from this organization's SoA. The AI
    // is no longer shown the catalogue in the prompt — it operates from its
    // training knowledge of Annex A — but the post-response filter rejects
    // any ID the consultant has marked as excluded, so the SoA remains the
    // source of truth for what gets persisted.
    let applicableControls = [];
    try {
      const data = await readOrgFile(org.id, 'controls/soa.json');
      const all = Array.isArray(data && data.controls) ? data.controls : [];
      applicableControls = all
        .filter((c) => c.applicable === true)
        .map((c) => ({ id: c.id, title: c.title, category: c.category }));
    } catch {
      applicableControls = [];
    }
    if (applicableControls.length === 0) {
      setAiErrorTreatment(t.aiError);
      setAiSuggestingTreatment(false);
      return;
    }

    // ── Layer 1: structured non-PII enrichment ────────────────────────────
    // Always-on aggregates derived from linked Assets/Suppliers/Incidents.
    // Enum values and counts only — never names, ids, or free text. Each
    // collection is independently best-effort: a single load failure omits
    // its aggregate but does not abort the AI call. Freshness over caching:
    // these aggregates run inside the click handler, not via useMemo.
    const [assetsAll, suppliersAll, incidentsAll] = await Promise.all([
      listOrgCollection(org.id, 'assets').catch(() => []),
      listOrgCollection(org.id, 'suppliers').catch(() => []),
      listOrgCollection(org.id, 'incidents').catch(() => []),
    ]);

    const ASSET_TYPE_ORDER = ['Information', 'Software', 'Hardware', 'Service', 'People', 'Premises', 'Other'];
    const CLASSIFICATION_ORDER = ['Public', 'Internal', 'Confidential', 'Restricted'];
    const LEVEL_ORDER = ['Critical', 'High', 'Medium', 'Low'];
    const orderedUnique = (values, order) => {
      const present = new Set(values.filter((v) => typeof v === 'string' && v.length > 0));
      return order.filter((v) => present.has(v));
    };
    const countsLine = (values, order) => {
      const counts = {};
      values.forEach((v) => { if (typeof v === 'string' && v.length > 0) counts[v] = (counts[v] || 0) + 1; });
      return order.filter((k) => counts[k] > 0).map((k) => `${k}×${counts[k]}`).join(', ');
    };

    const linkedAssets = assetsAll.filter((a) => form.linkedAssetIds.includes(a.id));
    const linkedSuppliers = suppliersAll.filter((s) => form.linkedSupplierIds.includes(s.id));
    const linkedIncidents = incidentsAll.filter((i) => form.linkedIncidentIds.includes(i.id));

    const layer1Lines = [];
    if (linkedAssets.length > 0) {
      const types = orderedUnique(linkedAssets.map((a) => a.type), ASSET_TYPE_ORDER);
      const classifications = orderedUnique(linkedAssets.map((a) => a.classification), CLASSIFICATION_ORDER);
      const critCounts = countsLine(linkedAssets.map((a) => a.criticality), LEVEL_ORDER);
      if (types.length > 0) layer1Lines.push(`Affected asset types: ${types.join(', ')}`);
      if (classifications.length > 0) layer1Lines.push(`Affected asset classifications: ${classifications.join(', ')}`);
      if (critCounts) layer1Lines.push(`Affected asset criticality: ${critCounts}`);
    }
    if (linkedSuppliers.length > 0) {
      const critCounts = countsLine(linkedSuppliers.map((s) => s.criticality), LEVEL_ORDER);
      const supplierLine = critCounts
        ? `Suppliers involved: ${linkedSuppliers.length} (${critCounts})`
        : `Suppliers involved: ${linkedSuppliers.length}`;
      layer1Lines.push(supplierLine);
    }
    if (linkedIncidents.length > 0) {
      const cats = [];
      linkedIncidents.forEach((i) => {
        if (Array.isArray(i.category)) i.category.forEach((c) => { if (typeof c === 'string' && c.length > 0) cats.push(c); });
      });
      const distinctCats = Array.from(new Set(cats));
      const sevCounts = countsLine(linkedIncidents.map((i) => i.severity), LEVEL_ORDER);
      if (distinctCats.length > 0) layer1Lines.push(`Past incident categories: ${distinctCats.join(', ')}`);
      if (sevCounts) layer1Lines.push(`Past incident severity: ${sevCounts}`);
    }

    // Always-on likelihood/impact labels. The prompt is in English regardless
    // of the consultant's lang setting, so always read T.en labels.
    const liLabel = T.en.liLabels[form.likelihood - 1] || '';
    const imLabel = T.en.imLabels[form.impact - 1] || '';
    const composedScoreLine = `Likelihood: ${form.likelihood} (${liLabel}) — Impact: ${form.impact} (${imLabel}) — Inherent score: ${inherentScore}`;

    const layer1Block = layer1Lines.length > 0
      ? `${composedScoreLine}\n${layer1Lines.join('\n')}`
      : composedScoreLine;

    // ── Layer 2: opt-in extended context ──────────────────────────────────
    // Added only when the per-call scope is "extended" (selected by the
    // consultant in the AI scope dialog before this handler runs). The four
    // labelled lines are emitted only for non-empty fields; the header itself
    // is omitted if all four are empty.
    const useExtended = scope === 'extended';
    let layer2Block = '';
    if (useExtended) {
      const extLines = [];
      if (form.description && form.description.trim()) extLines.push(`Description: ${form.description}`);
      if (form.likelihoodRationale && form.likelihoodRationale.trim()) extLines.push(`Likelihood rationale: ${form.likelihoodRationale}`);
      if (form.impactRationale && form.impactRationale.trim()) extLines.push(`Impact rationale: ${form.impactRationale}`);
      if (form.existingControls && form.existingControls.trim()) extLines.push(`Existing controls: ${form.existingControls}`);
      if (extLines.length > 0) {
        layer2Block = `\n\nExtended context (provided by the consultant):\n${extLines.join('\n')}`;
      }
    }

    const currentTreatmentLine = form.treatmentOption
      ? `\nCurrent treatment option: ${form.treatmentOption}`
      : '';

    const prompt = `You are an ISO 27001:2022 risk treatment expert.\nGiven the following risk, suggest the most appropriate treatment.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nRisk summary: ${form.riskSummary}\nRisk type (CIA): ${form.riskType.join(', ')}\nCategory: ${form.category}\n${layer1Block}${currentTreatmentLine}${layer2Block}\n\nlinkedControlIds rules:\n- linkedControlIds must contain IDs from ISO/IEC 27001:2022 Annex A.\n- Annex A IDs use the format chapter.number where chapter is 5 (Organizational), 6 (People), 7 (Physical), or 8 (Technological); examples: "5.2", "5.37", "8.13".\n- Return IDs as plain strings without the "A." prefix (e.g. "5.2", not "A.5.2").\n- Return only controls that directly mitigate this specific risk scenario. Prefer quality over quantity. If no Annex A control directly mitigates the risk, return an empty array — an empty array is the correct answer in that case, do not pad the list with loosely related controls.\n- For Retain or Avoid treatments the array is typically empty, because those treatments accept the risk as-is or remove the activity rather than introducing new controls.\n\nproposedTreatmentAction rules:\n- proposedTreatmentAction must be returned as an array of one or more strings. Each element is a single concise sentence describing one concrete actionable step (typically 10 to 25 words per element).\n- Each element must close a concrete gap, root cause, missing safeguard, or unmitigated exposure that is stated or clearly implied in the risk text — that is, in the Risk summary, Description, Likelihood rationale, or Impact rationale fields above. Before including an element, identify the specific sentence or phrase in the risk text that describes the gap the action closes. If no such sentence or phrase exists, do not include the action.\n- Exclude the following types of actions, even if they would be reasonable best-practice advice in a broader engagement. These categories are out of scope for a risk-specific treatment plan:\n  - Documentation, formalization, or restatement of controls already listed in the Existing controls field. That field is the system of record for current safeguards and is not duplicated in the treatment plan.\n  - programmatic, organization-wide, or scope-broadening actions (e.g. building a supplier-review program for an entire asset class, rolling out a company-wide policy, establishing recurrent review cycles for a whole category of suppliers or processes) unless the absence of that program or policy is explicitly identified in the risk text as the gap being mitigated. A risk about one specific supplier is not closed by recommending a program for all suppliers.\n  - Verification, monitoring, or follow-up actions whose sole purpose is to confirm the other treatment actions in this array were executed. Routine execution oversight is part of ISMS operation, not part of the treatment plan.\n  - Generic best practices such as awareness training, broad policy creation, culture changes, or general security improvements, unless the risk text explicitly identifies the absence of that specific best practice as the gap.\n- The number of elements is determined by the number of distinct gaps identified in the risk text, not by a target count and not by consultant diligence. If the risk text identifies one gap, return one element. If it identifies three distinct gaps, return three. Do not pad the array to appear more comprehensive. Do not split a single gap across multiple elements to inflate the array. Do not collapse genuinely distinct gaps into one element to compress the array.\n- Each element starts with an imperative verb (e.g. "Implement...", "Document...", "Configure...", "Review...", "Train...", "Establish..."). Each element is self-contained and understandable without reading the other elements.\n- Do not prefix elements with numbers, bullets, dashes, or any list marker. The consumer adds numbering when rendering. Return raw sentences only.\n- For Retain treatments the array typically has one element acknowledging the residual risk acceptance and any monitoring action. For Avoid treatments the array typically has one element describing the activity removal or boundary change.\n\nBefore producing the JSON, perform the following four reasoning steps and express each one in the reasoning field of the response. The application discards the reasoning field — it exists solely to force structured analysis before control selection, not to be displayed:\n- domain: whether the risk primarily affects information, people, physical assets, or ICT infrastructure (one sentence).\n- ciaProperty: which of confidentiality, integrity, availability, or a combination is the dominant property at risk (one sentence).\n- informationAsset: the implicit or explicit information asset affected (one sentence).\n- controlSelection: the mapping from risk scenario to chosen Annex A controls, with a brief justification per chosen control (2–3 sentences).\n\nReturn exactly this JSON, with keys in this order:\n{\n"reasoning": {\n"domain": "...",\n"ciaProperty": "...",\n"informationAsset": "...",\n"controlSelection": "..."\n},\n"treatmentOption": "Modify|Avoid|Share|Retain",\n"proposedTreatmentAction": ["Implement first concrete action starting with an imperative verb.", "Document the second concrete action starting with an imperative verb."],\n"linkedControlIds": ["5.2", "5.37"]\n}`;
    const result = await callWorker({ prompt, maxTokens: 1200 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorTreatment(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingTreatment(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorTreatment(t.aiError);
      setAiSuggestingTreatment(false);
      return;
    }
    if (form.treatmentOption === 'Modify' && ['Modify','Avoid','Share','Retain'].includes(parsed.treatmentOption)) setField('treatmentOption')(parsed.treatmentOption);
    if (!form.proposedTreatmentAction) {
      let normalizedAction = '';
      if (Array.isArray(parsed.proposedTreatmentAction)) {
        const items = parsed.proposedTreatmentAction
          .map((x) => String(x == null ? '' : x).trim())
          .filter((x) => x.length > 0);
        if (items.length === 1) {
          normalizedAction = items[0];
        } else if (items.length >= 2) {
          normalizedAction = items.map((t, i) => `${i + 1}. ${t}`).join('\n');
        }
      } else if (typeof parsed.proposedTreatmentAction === 'string') {
        normalizedAction = parsed.proposedTreatmentAction.trim();
      }
      if (normalizedAction) setField('proposedTreatmentAction')(normalizedAction);
    }
    if (Array.isArray(parsed.linkedControlIds) && form.linkedControlIds.length === 0) {
      const applicableSet = new Set(applicableControls.map((c) => c.id));
      const filtered = parsed.linkedControlIds.filter((x) => typeof x === 'string' && applicableSet.has(x));
      if (filtered.length > 0) setField('linkedControlIds')(filtered);
    }
    setAiSuggestingTreatment(false);
  };

  // Click wrapper for the Section 3 AI button. Always opens the scope dialog
  // with the safe default re-applied; never calls the worker directly. The
  // worker call happens only after the consultant confirms a scope.
  const handleAISuggestTreatmentClick = () => {
    setAiPromptScope('basic');
    setAiPromptDialogOpen(true);
  };

  const handleAIPromptConfirm = () => {
    setAiPromptDialogOpen(false);
    handleAISuggestTreatment(aiPromptScope);
  };

  const cols = winWidth >= 720 ? 2 : 1;
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: '0 1.5rem',
  };

  const isView = mode === 'view';
  const disabled = saveState === 'saving' || isView;
  const padX = winWidth >= 720 ? '2.5rem' : '1rem';
  const padY = winWidth >= 720 ? '2rem' : '1.5rem';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: BG_SURFACE,
      border: `1px solid ${BORDER_DEFAULT}`,
      borderRadius: 12,
      padding: `${padY} ${padX}`,
      position: 'relative',
    }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={t.btnConfirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />
      <ConfirmDialog
        open={aiPromptDialogOpen}
        onConfirm={handleAIPromptConfirm}
        onCancel={() => setAiPromptDialogOpen(false)}
        confirmLabel={t.aiPromptScopeSend}
        cancelLabel={t.aiPromptScopeCancel}
        confirmColor={BTN_PRIMARY}
        content={
          <div>
            <h3 style={{ ...FONT_BODY_STRONG, color: TEXT_HEADING, margin: '0 0 0.5rem' }}>
              {t.aiPromptScopeTitle}
            </h3>
            <p style={{ ...FONT_BODY, color: TEXT_DEFAULT, margin: '0 0 1rem', lineHeight: 1.55 }}>
              {t.aiPromptScopeIntro}
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.6rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="ai-prompt-scope"
                value="basic"
                checked={aiPromptScope === 'basic'}
                onChange={() => setAiPromptScope('basic')}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                {t.aiPromptScopeBasic}
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.4rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="ai-prompt-scope"
                value="extended"
                checked={aiPromptScope === 'extended'}
                onChange={() => setAiPromptScope('extended')}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                {t.aiPromptScopeExtended}
              </span>
            </label>
            <p style={{ ...FONT_SMALL, color: TEXT_MUTED, margin: '0 0 0 1.6rem', lineHeight: 1.45 }}>
              {t.aiPromptScopeExtendedHelp}
            </p>
          </div>
        }
      />
      <button
        type="button"
        onClick={onCancel}
        style={{
          ...FONT_SMALL,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: BRAND_PRIMARY, marginBottom: '0.75rem',
        }}
      >
        {t.backToList}
      </button>
      <h2 style={{
        ...FONT_PAGE_TITLE,
        color: TEXT_HEADING,
        margin: '0 0 0.5rem',
      }}>
        {isView ? t.titleView : (currentMode === 'create' ? t.titleCreate : t.titleEdit)}
      </h2>
      {!isView && (
        <p style={{
          ...FONT_SMALL,
          color: ERROR_TEXT,
          margin: '0 0 2rem',
          letterSpacing: '0.04em',
        }}>
          {t.requiredLegend}
        </p>
      )}

      {/* ── Sticky tab bar ────────────────────────────────────────────────── */}
      {/* Sits OUTSIDE the disabled fieldset so its buttons stay clickable in
          view mode. Mirrors the action bar's edge-to-edge inset (padX) and
          z-index; pinned to the top of the scrolling form. */}
      <div style={{
        position: 'sticky',
        top: NAVBAR_HEIGHT,
        marginLeft: `-${padX}`,
        marginRight: `-${padX}`,
        marginBottom: '1.5rem',
        padding: `0 ${padX}`,
        background: BG_SURFACE,
        borderBottom: `1px solid ${BORDER_DEFAULT}`,
        zIndex: 10,
        display: 'flex',
      }}>
        {[
          { id: 'identification', label: t.tabIdentification },
          { id: 'treatment',      label: t.tabTreatment },
          { id: 'relationships',  label: t.tabRelationships },
        ].map((tab) => {
          const active = activeTab === tab.id;
          const tabHasError = Object.keys(errors).some((k) => FIELD_TAB[k] === tab.id);
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                ...FONT_BUTTON,
                background: 'transparent',
                border: 'none',
                borderBottom: active ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
                color: active ? BRAND_PRIMARY : TEXT_MUTED,
                padding: '0.6rem 1rem',
                cursor: 'pointer',
                marginBottom: -1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              {tab.label}
              {tabHasError && (
                <span style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: ERROR_TEXT,
                  display: 'inline-block',
                }} />
              )}
            </button>
          );
        })}
      </div>

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {activeTab === 'identification' && (
      <>
      {/* ── SECTION 1 ─────────────────────────────────────────────────────── */}
      <p style={sectionHeading}>{t.s1}</p>
      <div style={gridStyle}>
        {/* Ref */}
        <FieldBlock>
          <label style={labelStyle}>{t.fRef}</label>
          <input
            type="text"
            value={ref || '…'}
            readOnly
            style={{ ...inputStyle(false), background: BG_SUBTLE, color: TEXT_MUTED }}
          />
        </FieldBlock>

        {/* Owner */}
        <FieldBlock>
          <label style={labelStyle}>{t.fOwner} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <div id="risk-field-owner">
            <PeopleSelector
              orgId={org.id}
              lang={lang}
              value={form.owner}
              onChange={setField('owner')}
            />
          </div>
          <ErrorMsg msg={errors.owner} />
        </FieldBlock>

        {/* Risk Summary */}
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fSummary} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <input
            id="risk-field-riskSummary"
            type="text"
            value={form.riskSummary}
            onChange={handleInputChange('riskSummary')}
            disabled={disabled}
            style={inputStyle(!!errors.riskSummary)}
          />
          <ErrorMsg msg={errors.riskSummary} />
        </FieldBlock>

        {/* Description */}
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fDescription}</label>
          <textarea
            value={form.description}
            onChange={handleInputChange('description')}
            disabled={disabled}
            style={textareaStyle(false)}
          />
        </FieldBlock>

        {/* Classification heading + AI Suggest */}
        <div style={{ gridColumn: '1 / -1' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', flexWrap: 'wrap', ...sectionHeading }}>
            <span>{t.aiClassificationHeading}</span>
            <AISuggestButton onClick={handleAISuggest} loading={aiSuggesting} disabled={disabled || !form.riskSummary.trim() || !org.aiProcessingConsent} lang={lang} />
            {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
          </div>
          {aiError && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiError}</p>}
        </div>

        {/* Risk Type (CIA) */}
        <FieldBlock>
          <label style={labelStyle}>{t.fRiskType}<InfoBadge text={t.ttRiskType} /><span className="ai-spark">✦</span></label>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { code: 'C', label: 'Confidentiality', color: '#1d4ed8', bg: STATUS_INFO.bg },
              { code: 'I', label: 'Integrity',       color: '#6d28d9', bg: '#ede9fe' },
              { code: 'A', label: 'Availability',    color: '#c2410c', bg: '#ffedd5' },
            ].map(({ code, label, color, bg }) => {
              const active = form.riskType.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleCIA(code)}
                  disabled={disabled}
                  style={{
                    ...FONT_SMALL,
                    background: active ? bg : SURFACE_WHITE,
                    color: active ? color : TEXT_MUTED,
                    border: `1px solid ${active ? color : BORDER_DEFAULT}`,
                    borderRadius: 6,
                    padding: '0.3rem 0.7rem',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontWeight: active ? 700 : 400,
                    transition: 'all 0.12s',
                  }}
                  title={label}
                >
                  {code}
                </button>
              );
            })}
          </div>
        </FieldBlock>

        {/* Category */}
        <FieldBlock>
          <label style={labelStyle}>{t.fCategory}<InfoBadge text={t.ttCategory} /><span className="ai-spark">✦</span></label>
          <input
            type="text"
            list="risk-categories"
            value={form.category}
            onChange={handleInputChange('category')}
            disabled={disabled}
            style={inputStyle(false)}
          />
          <datalist id="risk-categories">
            {t.categoryOptions.map((c) => <option key={c} value={c} />)}
          </datalist>
        </FieldBlock>

        {/* Existing Controls */}
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fExistingControls}<InfoBadge text={t.ttExistingControls} /></label>
          <textarea
            value={form.existingControls}
            onChange={handleInputChange('existingControls')}
            disabled={disabled}
            style={textareaStyle(false)}
          />
        </FieldBlock>

        {/* Identified Date */}
        <FieldBlock>
          <label style={labelStyle}>{t.fIdentifiedDate}<InfoBadge text={t.ttIdentifiedDate} /></label>
          <input
            type="date"
            value={form.identifiedDate || ''}
            onChange={(e) => setField('identifiedDate')(e.target.value || null)}
            disabled={disabled}
            style={inputStyle(false)}
          />
        </FieldBlock>

        {/* Identified By */}
        <FieldBlock>
          <label style={labelStyle}>{t.fIdentifiedBy}<InfoBadge text={t.ttIdentifiedBy} /></label>
          <PeopleSelector
            orgId={org.id}
            lang={lang}
            value={form.identifiedBy}
            onChange={setField('identifiedBy')}
            disabled={disabled}
          />
        </FieldBlock>

      </div>

      {/* ── SECTION 2 ─────────────────────────────────────────────────────── */}
      <p style={{ ...sectionHeading, marginTop: '1.5rem' }}>{t.s2}</p>
      <div style={gridStyle}>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fLikelihood}<InfoBadge text={t.ttLikelihood} /></label>
          <RatingSelector
            value={form.likelihood}
            onChange={setField('likelihood')}
            labels={t.liLabels}
            disabled={disabled}
          />
        </FieldBlock>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fLikelihoodRat}<InfoBadge text={t.ttLikelihoodRat} /></label>
          <textarea
            value={form.likelihoodRationale}
            onChange={handleInputChange('likelihoodRationale')}
            disabled={disabled}
            style={{ ...textareaStyle(false), minHeight: 56 }}
          />
        </FieldBlock>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fImpact}<InfoBadge text={t.ttImpact} /></label>
          <RatingSelector
            value={form.impact}
            onChange={setField('impact')}
            labels={t.imLabels}
            disabled={disabled}
          />
        </FieldBlock>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fImpactRat}<InfoBadge text={t.ttImpactRat} /></label>
          <textarea
            value={form.impactRationale}
            onChange={handleInputChange('impactRationale')}
            disabled={disabled}
            style={{ ...textareaStyle(false), minHeight: 56 }}
          />
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fInherentScore}<InfoBadge text={t.ttInherentScore} /></label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
              {form.likelihood} × {form.impact} =
            </span>
            <ScoreBadge score={inherentScore} level={inherentLevel} t={t} />
          </div>
        </FieldBlock>
      </div>
      </>
      )}

      {activeTab === 'treatment' && (
      <>
      {/* ── SECTION 3 ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', ...sectionHeading, marginTop: '1.5rem' }}>
        <span>{t.s3}</span>
        <AISuggestButton onClick={handleAISuggestTreatmentClick} loading={aiSuggestingTreatment} disabled={disabled || !form.riskSummary.trim() || !org.aiProcessingConsent} lang={lang} />
        {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
      </div>
      {aiErrorTreatment && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiErrorTreatment}</p>}
      <div style={gridStyle}>
        <FieldBlock>
          <label style={labelStyle}>{t.fTreatmentOption}<InfoBadge text={t.ttTreatmentOption} /><span className="ai-spark">✦</span></label>
          <select value={form.treatmentOption} onChange={handleInputChange('treatmentOption')} disabled={disabled} style={selectStyle}>
            {t.treatmentOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fActionStatus}</label>
          <select value={form.treatmentActionStatus} onChange={handleInputChange('treatmentActionStatus')} disabled={disabled} style={selectStyle}>
            {t.actionStatusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldBlock>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fProposedAction}<InfoBadge text={t.ttProposedAction} /><span className="ai-spark">✦</span></label>
          <textarea value={form.proposedTreatmentAction} onChange={handleInputChange('proposedTreatmentAction')} disabled={disabled} style={textareaStyle(false)} />
        </FieldBlock>
        {Array.isArray(org.norms) && org.norms.length > 1 && (
          <FieldBlock>
            <label style={labelStyle}>{t.fControlRefs}<InfoBadge text={t.ttControlRefs} /><span className="ai-spark">✦</span></label>
            <TagInput
              tags={form.controlReferences}
              onAdd={(v) => setField('controlReferences')([...form.controlReferences, v])}
              onRemove={(v) => setField('controlReferences')(form.controlReferences.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('controlReferences')(form.controlReferences.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderControlRefs}
              disabled={disabled}
            />
          </FieldBlock>
        )}
        <FieldBlock>
          <label style={labelStyle}>{t.fLinkedControls}<InfoBadge text={t.ttLinkedControls} /><span className="ai-spark">✦</span></label>
          {controlOptions.length > 0 ? (
            <EntitySelector
              items={controlOptions}
              selectedIds={form.linkedControlIds}
              onAdd={(id) => setField('linkedControlIds')([...form.linkedControlIds, id])}
              onRemove={(id) => setField('linkedControlIds')(form.linkedControlIds.filter((x) => x !== id))}
              placeholder={t.searchControl}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedControlIds}
              onAdd={(v) => setField('linkedControlIds')([...form.linkedControlIds, v])}
              onRemove={(v) => setField('linkedControlIds')(form.linkedControlIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('linkedControlIds')(form.linkedControlIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.searchControl}
              disabled={disabled}
            />
          )}
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fActionOwner}</label>
          <PeopleSelector orgId={org.id} lang={lang} value={form.treatmentActionOwner} onChange={setField('treatmentActionOwner')} multiple={true} />
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fTreatmentApprovedBy}<InfoBadge text={t.ttTreatmentApprovedBy} /></label>
          <div id="risk-field-treatmentApprovedBy">
            <PeopleSelector orgId={org.id} lang={lang} value={form.treatmentApprovedBy} onChange={setField('treatmentApprovedBy')} disabled={disabled} />
          </div>
          {form.treatmentApprovedBy && form.owner && form.treatmentApprovedBy === form.owner && (
            <>
              <p style={{ ...FONT_SMALL, color: STATUS_WARN.text, margin: '0.2rem 0 0' }}>
                {t.noticeApproverEqualsOwner}
              </p>
              <div style={{ marginTop: '0.5rem' }}>
                <label style={labelStyle}>{t.fSodException}<InfoBadge text={t.ttSodException} /></label>
                <textarea value={form.sodExceptionJustification} onChange={handleInputChange('sodExceptionJustification')} disabled={disabled} style={textareaStyle(false)} />
              </div>
            </>
          )}
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fTreatmentApprovalDate}<InfoBadge text={t.ttTreatmentApprovalDate} /></label>
          <input type="date" value={form.treatmentApprovalDate || ''} onChange={(e) => setField('treatmentApprovalDate')(e.target.value || null)} disabled={disabled} style={inputStyle(false)} />
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fTreatmentDeadline}<InfoBadge text={t.ttTreatmentDeadline} /></label>
          <input type="date" value={form.treatmentDeadline || ''} onChange={handleInputChange('treatmentDeadline')} disabled={disabled} style={inputStyle(false)} />
        </FieldBlock>
        <FieldBlock span2={cols === 2}>
          <label style={labelStyle}>{t.fProgress}<InfoBadge text={t.ttProgress} /></label>
          <textarea value={form.treatmentActionProgress} onChange={handleInputChange('treatmentActionProgress')} disabled={disabled} style={textareaStyle(false)} />
        </FieldBlock>
      </div>

      {/* ── SECTION 4 ─────────────────────────────────────────────────────── */}
      <p style={{ ...sectionHeading, marginTop: '1.5rem' }}>{t.s4}</p>
      {postTreatmentUnlocked ? (
        <div style={gridStyle}>
          <FieldBlock span2={cols === 2}>
            <label style={labelStyle}>{t.fResidualLik}<InfoBadge text={t.ttResidualLik} /></label>
            <RatingSelector value={form.residualLikelihood} onChange={setField('residualLikelihood')} labels={t.liLabels} disabled={disabled} />
          </FieldBlock>
          <FieldBlock span2={cols === 2}>
            <label style={labelStyle}>{t.fResidualImp}<InfoBadge text={t.ttResidualImp} /></label>
            <RatingSelector value={form.residualImpact} onChange={setField('residualImpact')} labels={t.imLabels} disabled={disabled} />
          </FieldBlock>
          <FieldBlock>
            <label style={labelStyle}>{t.fResidualScore}<InfoBadge text={t.ttResidualScore} /></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ ...FONT_LABEL, color: TEXT_DEFAULT }}>
                {form.residualLikelihood ?? '—'} × {form.residualImpact ?? '—'} =
              </span>
              {residualScore != null ? <ScoreBadge score={residualScore} level={residualLevel} t={t} /> : <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>—</span>}
            </div>
          </FieldBlock>
        </div>
      ) : (
        <div style={{
          ...FONT_LABEL,
          background: BG_MUTED,
          border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 6,
          padding: '0.75rem 1rem',
          color: TEXT_MUTED,
        }}>
          {form.treatmentOption === 'Retain' ? t.s4LockedAccept : t.s4LockedApproval}
        </div>
      )}

      {/* ── SECTION 5 ─────────────────────────────────────────────────────── */}
      <p style={{ ...sectionHeading, marginTop: '1.5rem' }}>{t.s5}</p>
      <div style={gridStyle}>
        {form.treatmentOption !== 'Avoid' && (
          <FieldBlock span2={cols === 2}>
            <label style={labelStyle}>{t.fAcceptanceCrit}<InfoBadge text={t.ttAcceptanceCrit} /></label>
            <textarea value={form.acceptanceCriteria} onChange={handleInputChange('acceptanceCriteria')} disabled={disabled} style={textareaStyle(false)} />
          </FieldBlock>
        )}
        {/* Accepted checkbox — Retain-only acceptance fields. The `accepted`
            checkbox is the gate that unlocks the post-treatment assessment for
            Retain (see postTreatmentUnlocked); for Modify/Avoid/Share that gate
            is treatmentApprovalDate, so these fields have no operational meaning
            and are not rendered. */}
        {form.treatmentOption === 'Retain' && (
          <>
            <FieldBlock span2={cols === 2}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.accepted}
                  onChange={(e) => setField('accepted')(e.target.checked)}
                  disabled={disabled}
                  style={{ width: 16, height: 16, cursor: disabled ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                  {t.fAccepted}
                </span>
              </label>
            </FieldBlock>
            {form.accepted && (
              <>
                <FieldBlock>
                  <label style={labelStyle}>{t.fAcceptedBy}<InfoBadge text={t.ttAcceptedBy} /></label>
                  <PeopleSelector orgId={org.id} lang={lang} value={form.acceptedBy || ''} onChange={setField('acceptedBy')} disabled={disabled} />
                </FieldBlock>
                <FieldBlock>
                  <label style={labelStyle}>{t.fAcceptanceDate}<InfoBadge text={t.ttAcceptanceDate} /></label>
                  <input type="date" value={form.acceptanceDate} onChange={handleInputChange('acceptanceDate')} disabled={disabled} style={inputStyle(false)} />
                </FieldBlock>
              </>
            )}
          </>
        )}
        <FieldBlock>
          <label style={labelStyle}>{t.fStatus}<InfoBadge text={t.ttStatus} /></label>
          <select value={form.status} onChange={handleInputChange('status')} disabled={disabled} style={selectStyle}>
            {t.statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fNextReviewDate}<InfoBadge text={t.ttNextReviewDate} /></label>
          <input type="date" value={form.nextReviewDate || ''} onChange={(e) => setField('nextReviewDate')(e.target.value || null)} disabled={disabled} style={inputStyle(false)} />
        </FieldBlock>
        {/* Residual-acceptance field. Conceptual acceptance happens at the
            residual level only for Modify/Share; Retain has no residual distinct
            from the inherent risk and Avoid removes the activity, so this is not
            rendered for those two options. */}
        {(form.treatmentOption === 'Modify' || form.treatmentOption === 'Share') && (
          <FieldBlock span2={cols === 2}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: disabled ? 'not-allowed' : 'pointer' }}>
              <input
                type="checkbox"
                checked={form.residualRiskAccepted}
                onChange={(e) => setField('residualRiskAccepted')(e.target.checked)}
                disabled={disabled}
                style={{ width: 16, height: 16, cursor: disabled ? 'not-allowed' : 'pointer' }}
              />
              <span style={{ ...FONT_BODY, color: TEXT_DEFAULT }}>
                {t.fResidualRiskAccepted}
                <InfoBadge text={t.ttResidualRiskAccepted} />
              </span>
            </label>
          </FieldBlock>
        )}
      </div>

      {/* Interview notes (collapsible) */}
      <div style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
        <button
          type="button"
          onClick={() => setShowNotes((v) => !v)}
          disabled={disabled}
          style={{
            ...FONT_SMALL,
            background: 'none', border: 'none', cursor: 'pointer',
            color: TEXT_MUTED, padding: 0, letterSpacing: '0.04em',
          }}
        >
          {showNotes ? t.hideNotes : t.showNotes} ▾
        </button>
        {showNotes && (
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>{t.fInterviewNotes}<InfoBadge text={t.ttInterviewNotes} /></label>
            <textarea
              value={form.interviewNotes}
              onChange={handleInputChange('interviewNotes')}
              disabled={disabled}
              style={{ ...textareaStyle(false), minHeight: 96 }}
            />
          </div>
        )}
      </div>
      </>
      )}

      {activeTab === 'relationships' && (
      <>
      {/* ── SECTION 6 ─────────────────────────────────────────────────────── */}
      <p style={{ ...sectionHeading, marginTop: '1.5rem' }}>{t.s6}</p>
      <div style={gridStyle}>
        <FieldBlock>
          <label style={labelStyle}>{t.fLinkedAssetIds}</label>
          {assetOptions.length > 0 ? (
            <EntitySelector
              items={assetOptions}
              selectedIds={form.linkedAssetIds}
              onAdd={(id) => setField('linkedAssetIds')([...form.linkedAssetIds, id])}
              onRemove={(id) => setField('linkedAssetIds')(form.linkedAssetIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedAssetIds}
              onAdd={(v) => setField('linkedAssetIds')([...form.linkedAssetIds, v])}
              onRemove={(v) => setField('linkedAssetIds')(form.linkedAssetIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('linkedAssetIds')(form.linkedAssetIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fLinkedSupplierIds}</label>
          {supplierOptions.length > 0 ? (
            <EntitySelector
              items={supplierOptions}
              selectedIds={form.linkedSupplierIds}
              onAdd={(id) => setField('linkedSupplierIds')([...form.linkedSupplierIds, id])}
              onRemove={(id) => setField('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedSupplierIds}
              onAdd={(v) => setField('linkedSupplierIds')([...form.linkedSupplierIds, v])}
              onRemove={(v) => setField('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('linkedSupplierIds')(form.linkedSupplierIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fLinkedIncidentIds}</label>
          {incidentOptions.length > 0 ? (
            <EntitySelector
              items={incidentOptions}
              selectedIds={form.linkedIncidentIds}
              onAdd={(id) => setField('linkedIncidentIds')([...form.linkedIncidentIds, id])}
              onRemove={(id) => setField('linkedIncidentIds')(form.linkedIncidentIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedIncidentIds}
              onAdd={(v) => setField('linkedIncidentIds')([...form.linkedIncidentIds, v])}
              onRemove={(v) => setField('linkedIncidentIds')(form.linkedIncidentIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('linkedIncidentIds')(form.linkedIncidentIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </FieldBlock>
        <FieldBlock>
          <label style={labelStyle}>{t.fLinkedNonconformityIds}</label>
          {ncOptions.length > 0 ? (
            <EntitySelector
              items={ncOptions}
              selectedIds={form.linkedNonconformityIds}
              onAdd={(id) => setField('linkedNonconformityIds')([...form.linkedNonconformityIds, id])}
              onRemove={(id) => setField('linkedNonconformityIds')(form.linkedNonconformityIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedNonconformityIds}
              onAdd={(v) => setField('linkedNonconformityIds')([...form.linkedNonconformityIds, v])}
              onRemove={(v) => setField('linkedNonconformityIds')(form.linkedNonconformityIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => setField('linkedNonconformityIds')(form.linkedNonconformityIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </FieldBlock>
      </div>
      </>
      )}

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: `-${padX}`,
          marginRight: `-${padX}`,
          marginBottom: `-${padY}`,
          marginTop: '1.5rem',
          padding: `0.85rem ${padX}`,
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {saveState === 'error' && (
            <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
              {t.errorSave}
            </div>
          )}
          {deleteError && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {deleteError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {t.errorRequiredFields}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              {currentMode === 'edit' && (
                <button
                  onClick={() => setConfirmState({ confirmLabel: t.btnDelete })}
                  disabled={disabled}
                  style={{
                    ...BTN_DESTRUCTIVE_OUTLINE,
                    ...FONT_SMALL,
                    borderRadius: 8,
                    padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t.btnDelete}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {dirty && (
                <span style={{
                  ...FONT_LABEL,
                  color: UNSAVED_AMBER,
                }}>{t.unsavedLabel}</span>
              )}
              <button
                onClick={onCancel}
                disabled={disabled}
                style={{
                  ...BTN_SECONDARY,
                  ...FONT_BUTTON,
                  fontWeight: 400,
                  borderRadius: 8, padding: '0.6rem 1.25rem',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {t.btnCancel}
              </button>
              <button
                onClick={handleSave}
                disabled={disabled}
                style={{
                  ...BTN_PRIMARY,
                  ...FONT_BUTTON,
                  fontWeight: 700,
                  background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
                  border: 'none', borderRadius: 8, padding: '0.6rem 1.6rem',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}
              </button>
            </div>
          </div>
        </div>
      )}
      </fieldset>
      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
